import useFormApi from '@data-driven-forms/react-form-renderer/dist/cjs/use-form-api';
import { AddButton } from '@td-client-frontend/common';
import { Add, SelectIcon } from '@td-client-frontend/icons';
import clsx from 'clsx';
import React, { useCallback } from 'react';
import ReactTooltip from 'react-tooltip';
import { useDdf } from '../context/ddf-context';
import { getGroupNumber, checkConditionsForCcmButton } from '../utils/groups/helpers';

/**
 * In case of purging css:
 * As long as a class name appears in your template in its entirety, PurgeCSS will not remove it.
 * https://tailwindcss.com/docs/controlling-file-size#writing-purgeable-html
 */
export const buttonKindMap: Record<string, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  link: 'btn-link',
  default: 'btn-default'
};

interface ButtonProps {
  kind?: string;
  action?: string;
  onClick?: (name?: string, action?: string, type?: HTMLButtonElement['type']) => void;
  className?: string;
  children?: React.ReactNode;
  tooltip?: string;
}

export function Button({
  disabled,
  name,
  title,
  children,
  kind = 'primary',
  type = 'button',
  className,
  onClick,
  id,
  tooltip,
  ...props
}: ButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const needTooltip = disabled && tooltip;

  return (
    <>
      <span data-tip={tooltip} data-for={id}>
        <button
          id={id}
          name={name}
          type={type}
          disabled={disabled}
          onClick={onClick}
          className={clsx('btn', buttonKindMap[kind], 'focus:outline-none focus-visible:ring', className)}
          {...props}
        >
          {children || title}
        </button>
      </span>
      {needTooltip && (
        <ReactTooltip
          id={id}
          className="tooltip-custom"
          place="top"
          effect="solid"
          type="light"
          multiline
          border
          borderColor="#5b2f91"
        />
      )}
    </>
  );
}

/* istanbul ignore next */ /* this code is not used or will be deleted in other branches */
export function ButtonDDf(props: any) {
  const { getState, change, reset } = useFormApi();
  const { handlers, setSubForm, groupType } = useDdf();

  const handleButtonClick = useCallback(
    (name, action, type) => {
      handlers[action]?.(getState().values, change, setSubForm, reset, getState);
    },
    [handlers, getState, change, setSubForm, reset]
  );

  const { disabled, name, action, type, hiddenForTypes } = props;

  const hideForGroupType = hiddenForTypes?.map((t: string) => t.toLowerCase()).includes(groupType);

  // condition for schedulePriceUpdateCCM button
  if (name?.includes('schedulePriceUpdateCCM')) {
    const groupNumber = getGroupNumber(name);
    const state = getState().initialValues.groups[groupNumber];
    const showCcmPriceUpdate = checkConditionsForCcmButton(state, hideForGroupType);
    return showCcmPriceUpdate ? (
      <Button {...props} onClick={() => !disabled && action && handleButtonClick(name, action, type)} />
    ) : null;
  }

  return !hiddenForTypes || !hideForGroupType ? (
    <Button {...props} onClick={() => !disabled && action && handleButtonClick(name, action, type)} />
  ) : null;
}

// should be extended once we need more icons to use
const buttonIconMap: Record<string, JSX.Element> = {
  back: <SelectIcon className="inline" transform="rotate(90)" />,
  add: <Add />
};
/* istanbul ignore next */ /* this code is not used or will be deleted in other branches */
export function ButtonWithIconDDf(props: any) {
  const { getState, change, reset } = useFormApi();
  const { handlers } = useDdf();
  const { icon, action, ...restProps } = props;
  const buttonIcon = buttonIconMap[props.icon];

  const handleButtonClick = useCallback(() => {
    handlers[action]?.(getState().values, change, reset, getState);
  }, [handlers, action, getState, change, reset]);

  return <AddButton {...restProps} Icon={buttonIcon} onClick={handleButtonClick} />;
}
